import AdminLayout from "../../app-components/AdminLayout";
import { useState, useEffect } from "react";
import apiClient from "../../api-services/ApiClient";
import Alert from "../../app-components/alert/Alert";
import Preloader from "../../app-components/preloader/Preloader";
import {
  Icon_Caret_Left,
  Icon_Caret_Right,
  Icon_Dots,
} from "../../assets/Icons";
import { ParamInterface } from "../../interface/users.interface";
import { formatDate } from "../../utils";
import { tinyAlert } from "../../utils/SweetAlert";

// "new" = compose form only, "history" = sent broadcasts table only
type BroadcastView = "new" | "history";

const Broadcast = ({ view = "new" }: { view?: BroadcastView }) => {
  const [mesageInput, setMessageInput] = useState<string>("");
  const [titleInput, setTitleInput] = useState<string>("");
  const [contactInput, setContactInput] = useState<string>("");
  const [type, setType] = useState<string>("");
  const [broadcastType, setBroadcastType] = useState<string>("");
  const [broadcastData, setBroadcastData] = useState<any[]>([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(0);

  const [searchInput, setSearchInput] = useState<string>("");
  const [searchInputDebounce, setSearchInputDebounce] = useState<string>("");
  const [dateRange, setDateRange] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [alertMessage, setAlertMessage] = useState("");

  // effect for get users list
  useEffect(() => {
    // the compose view doesn't show the table, so skip loading it
    if (view !== "history") return;

    const params = {
      page: currentPage,
      search: searchInput,
      dateRange: dateRange,
      broadcastType: broadcastType,
    };

    getBroadcast(params);
  }, [view, currentPage, searchInput, dateRange, broadcastType]);

  // debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchInput(searchInputDebounce);
      setCurrentPage(1);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [searchInputDebounce]);

  useEffect(() => {
    setAlertMessage("");
    console.log(startDate, endDate);

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (end > start) {
        setDateRange(startDate + "," + endDate);
        setCurrentPage(1);
      } else {
        setAlertMessage("Invalid date range.");
      }
    }
    if (!startDate && !endDate) {
      setDateRange("");
    }
  }, [startDate, endDate]);

  const getBroadcast = async (params: ParamInterface) => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("broadcasts", { params });
      if (res && res.data) {
        setBroadcastData(res.data.data);
        setCurrentPage(res.data.current_page);
        setLastPage(res.data.last_page);
      } else {
        setBroadcastData([]);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setAlertMessage("Unable to fetch record at the moment.");
      setBroadcastData([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle Previous Button Click
  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Handle Next Button Click
  const handleNext = () => {
    if (currentPage < lastPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      const data = {
        message: mesageInput,
        contact: contactInput,
        type: type,
        title: titleInput,
        status: "publish",
      };

      if (
        !data.message ||
        data.message.trim() === "" ||
        !data.contact ||
        !data.type ||
        !data.title
      ) {
        alert("all fields are required.");
        return;
      }

      await apiClient.post("broadcasts", data);

      setMessageInput("");
      setContactInput("");
      setType("");

      const params = {
        page: currentPage,
        search: searchInput,
        dateRange: dateRange,
        broadcastType: broadcastType,
      };

      getBroadcast(params);

      tinyAlert("success", "Broadcast Message Sent Successfully");
    } catch (error) {
      alert("error ocurred " + JSON.stringify(error));
    }
  };

  // Compute the starting serial number for the current page (page 1 -> 1, page 2 -> 11, ...)
  const serialStart = (currentPage - 1) * 10 + 1;

  return (
    <AdminLayout
      title={view === "new" ? "Admin | New Broadcast" : "Admin | Broadcast History"}
    >
      <div className="flex justify-between items-center mb-5">
        <p className="font-semibold text-[25px]">
          {view === "new" ? "New Broadcast" : "Broadcast History"}
        </p>
      </div>

      {/* <!-- TABLE --> */}
      <div className="py-5 bg-white rounded-lg shadow">
        {view === "new" && (
        <form
          onSubmit={handleSubmit}
          className="px-5"
        >
          <p className="px-5 mb-5 font-medium text-xl text-[#202224]">
            New Broadcast Message
          </p>
          {/* <!-- ROW --> */}
          <div className="px-5 mb-5">
            <textarea
              className="p-3 w-full rounded-lg bg-[#F8F8F8] border border-gray-300 text-honeConnectGray focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm h-[100px]"
              placeholder="Add Contacts (separated by comma)"
              value={contactInput}
              onChange={(e) => setContactInput(e.target.value)}
            ></textarea>
          </div>

          {/* <!-- ROW --> */}
          <div className="px-5 mb-5">
            <textarea
              className="p-3 w-full rounded-lg bg-[#F8F8F8] border border-gray-300 text-honeConnectGray focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm h-[50px]"
              placeholder="Enter Title"
              value={titleInput}
              onChange={(e) => setTitleInput(e.target.value)}
            ></textarea>
          </div>

          {/* <!-- ROW --> */}
          <div className="px-5 mb-5">
            <textarea
              className="p-3 w-full rounded-lg bg-[#F8F8F8] border border-gray-300 text-honeConnectGray focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm h-[100px]"
              placeholder="Add Message"
              value={mesageInput}
              onChange={(e) => setMessageInput(e.target.value)}
            ></textarea>
            <p className="mt-2 text-right text-sm font-normal">
              <span className="text-honeConnectPurple">
                {mesageInput.length}
              </span>{" "}
              Characters
            </p>
          </div>

          {/* <!-- ROW --> */}
          <div className="px-5 mb-10">
            <select
              className="bg-[#F8F8F8] text-honeConnectGray p-3 px-4 rounded-[5px] border-2 border-solid border-gray-300 text-sm font-normal flex items-center gap-3"
              onChange={(e) => {
                setType(e.target.value);
              }}
              defaultValue={""}
              value={type}
            >
              <option value={""} disabled>
                Type
              </option>
              <option value={"SMS"}>SMS</option>
              <option value={"Email"}>Email</option>
              <option value={"Push"}>Push Notification</option>
              <option value={"App"}>In App Notification</option>
            </select>

            <button
              type="submit"
              className="bg-honeConnectPurple text-white py-3 px-16 rounded-lg border-0 text-base mt-3"
            >
              Send Message
            </button>
          </div>
        </form>
        )}

        {view === "history" && (
        <>
        {/* <!-- filter --> */}
        <div className="px-5 pt-5  flex justify-between items-center mb-5 flex-wrap gap-y-3">
          <div className="relative w-1/2">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3">
              <svg
                className="h-5 w-5 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                ></path>
              </svg>
            </span>

            <input
              value={searchInputDebounce}
              onChange={(e) => setSearchInputDebounce(e.target.value)}
              type="text"
              className="pl-10 pr-4 py-3 w-full rounded-lg bg-[#F8F8F8] border border-[#F2F1F1] text-honeConnectGray focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm w-full"
              placeholder="Search..."
            />
          </div>

          <div className="flex gap-5 w-1/2 justify-end items-center">
            {/* Start date filter */}
            <div className="flex items-center gap-2 bg-[#F8F8F8] text-honeConnectGray py-3 pl-4 pr-8 rounded-[5px] border-2 border-solid border-[#F2F1F1] text-sm font-normal">
              <label htmlFor="start-date" className="whitespace-nowrap">
                Start
              </label>
              <input
                type="date"
                id="start-date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-transparent outline-none text-sm"
              />
            </div>

            {/* End date filter */}
            <div className="flex items-center gap-2 bg-[#F8F8F8] text-honeConnectGray py-3 pl-4 pr-8 rounded-[5px] border-2 border-solid border-[#F2F1F1] text-sm font-normal">
              <label htmlFor="end-date" className="whitespace-nowrap">
                End
              </label>
              <input
                type="date"
                id="end-date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-transparent outline-none text-sm"
              />
            </div>

            <select
              className="bg-[#F8F8F8] text-honeConnectGray p-3 px-4 rounded-[5px] border-2 border-solid border-[#F2F1F1] text-sm font-normal flex items-center gap-3"
              onChange={(e) => {
                setBroadcastType(e.target.value);
                setCurrentPage(1);
              }}
              defaultValue={""}
            >
              <option value={""} disabled>
                Type
              </option>
              <option value={""}>ALL</option>
              <option value={"SMS"}>SMS</option>
              <option value={"Email"}>Email</option>
              <option value={"Push"}>Push Notification</option>
              <option value={"App"}>In App Notification</option>
            </select>
          </div>
        </div>

        {/* <!-- record TABLE --> */}
        <div className="overflow-x-auto mb-5 overflow-y-auto">
          <table className="min-w-full bg-white mb-4">
            <thead>
              <tr className="w-full bg-[#E4E1E1] text-gray-600 capitalize text-sm leading-normal">
                <th className="py-3 px-6 text-left">S/N</th>
                <th className="py-3 px-6 text-left">Date</th>
                <th className="py-3 px-6 text-left">Type</th>
                <th className="py-3 px-6 text-left">Message</th>
                <th className="py-3 px-6 text-left">Recipient</th>
                <th className="py-3 px-6 text-left">Status</th>
                <th className="py-3 px-6 text-center hidden">Action</th>
              </tr>
            </thead>

            {!!broadcastData?.length && (
              <tbody className="text-gray-600 text-sm font-light">
                {broadcastData?.map((data: any, index: number) => {
                  return (
                    <tr
                      key={data.id}
                      className="border-b border-[#F2F1F1] hover:bg-gray-100 font-medium text-[#232630]"
                    >
                      <td className="py-3 px-6 text-left">
                        {serialStart + index}
                      </td>

                      <td className="py-3 px-6 text-left">
                        {data.created_at
                          ? formatDate(data.created_at, "date")
                          : ""}
                      </td>

                      <td className="py-3 px-6 text-left">{data?.type}</td>

                      <td className="py-3 px-6 text-left">{data?.message}</td>

                      <td className="py-3 px-6 text-left">{data?.contact}</td>

                      <td className="py-3 px-6 text-left">{data.status}</td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>

          {!broadcastData?.length && (
            <p className="text-center p-5">No record found</p>
          )}
        </div>

        {/* <!-- ROW PAGINATION --> */}
        <div className="flex justify-center gap-3 flex-wrap">
          {/* Previous Button */}
          <button
            onClick={handlePrevious}
            disabled={currentPage === 1}
            className={`bg-[#fff] text-honeConnectGray p-3 px-4 rounded-[14px] border-2 border-solid border-[#F2F1F1] text-base font-medium ${
              currentPage === 1
                ? "disabled:text-gray-400 disabled:cursor-not-allowed"
                : ""
            }`}
          >
            <span>Previous</span>
          </button>

          {/* Left Arrow */}
          <button
            disabled={currentPage === 1}
            onClick={handlePrevious}
            className={`bg-[#fff] text-honeConnectGray p-3 px-5 rounded-[14px] border-2 border-solid border-[#F2F1F1] text-base font-medium ${
              currentPage === 1
                ? "disabled:text-gray-400 disabled:cursor-not-allowed"
                : ""
            }`}
          >
            <img src={Icon_Caret_Left} alt="" />
          </button>

          {/* Page Numbers */}
          {Array.from(
            { length: lastPage > 7 ? 3 : lastPage },
            (_, i) => i + 1
          ).map((page) => (
            <button
              title={`page ${page}`}
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`p-3 px-5 rounded-[14px] border-2 border-solid border-[#F2F1F1] text-base font-medium ${
                page === currentPage
                  ? "bg-honeConnectPurple text-white"
                  : "bg-[#fff] text-honeConnectGray"
              }`}
            >
              <span>{page}</span>
            </button>
          ))}

          {lastPage > 7 && (
            <>
              {/* ellipse Arrow */}
              <button
                disabled
                className={`bg-[#fff] text-honeConnectGray p-3 px-5 rounded-[14px] border-2 border-solid border-[#F2F1F1] text-base font-medium`}
              >
                <img src={Icon_Dots} alt="" className="text-center" />
              </button>

              {/* Page Numbers */}
              {Array.from({ length: 3 }, (_, i) => 2 - i).map((page) => (
                <button
                  title={`page ${lastPage - page}`}
                  key={lastPage - page}
                  onClick={() => setCurrentPage(lastPage - page)}
                  className={`p-3 px-5 rounded-[14px] border-2 border-solid border-[#F2F1F1] text-base font-medium ${
                    lastPage - page === currentPage
                      ? "bg-honeConnectPurple text-white"
                      : "bg-[#fff] text-honeConnectGray"
                  }`}
                >
                  <span>{lastPage - page}</span>
                </button>
              ))}
            </>
          )}

          {/* Right Arrow */}
          <button
            disabled={currentPage === lastPage}
            onClick={handleNext}
            className={`bg-[#fff] text-honeConnectGray p-3 px-5 rounded-[14px] border-2 border-solid border-[#F2F1F1] text-base font-medium ${
              currentPage === lastPage
                ? "disabled:text-gray-400 disabled:cursor-not-allowed"
                : ""
            }`}
          >
            <img src={Icon_Caret_Right} alt="" />
          </button>

          {/* Next Button */}
          <button
            onClick={handleNext}
            disabled={currentPage === lastPage}
            className={`bg-[#fff] text-honeConnectGray p-3 px-4 rounded-[14px] border-2 border-solid border-[#F2F1F1] text-base font-medium ${
              currentPage === lastPage
                ? "disabled:text-gray-400 disabled:cursor-not-allowed"
                : ""
            }`}
          >
            <span>Next</span>
          </button>
        </div>
        </>
        )}
      </div>

      <Preloader loading={loading} />
      <Alert message={alertMessage} />
    </AdminLayout>
  );
};

export default Broadcast;